import { SELECT_OPTION_TYPE, STRING_ANY_HASH, STRING_STRING_HASH } from '../constants/globals';
import { KnownClearingHouses } from './BillingCode.type';
import type { ClaimQuickPickKey } from './ClinicBillingSettings.type';
import {
  ConnectionApiExchangeState,
  ConnectionApiTaskExchangeState,
} from './ConnectionApiEnrollment.type';
import type { ConnectorDrainTrigger } from './ConnectorAdmin.type';
import { ConnectorEnrollmentState } from './ConnectorParticipantProvisioning.type';
import { ClaimResponseEraStatus, EraStatus } from './Era.type';
import { InsuranceEligibilityRequestStatus } from './InsuranceEligibilityRequest.type';
import { type ClaimBuildContext, InvoiceStatusEnum } from './Invoice.type';
import type { Pseudo835Capability } from './Pseudo835.type';

export const ChiroUpIdPattern =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export enum ValidExchangeFunctionsEnum {
  ByType = 'by-type',
  Summary = 'summary',
  Single = 'single',
  Activity = 'activity',
  UIList = 'ui-list',
  Options = 'options',
  /** EDI Activity (12384625988) stat tiles over the same filters as UIList. */
  UIStats = 'ui-stats',
  /** EDI Activity filter options sourced from the clinic's exchange data. */
  UIFilterOptions = 'ui-filter-options',
  /** EDI Activity 999/277 detail from the ClinicTransactionEvent ledger. */
  LedgerDetail = 'ledger-detail',
  /** EDI Activity: the 999s that answered a claim file, or the claim files a 999 answered. */
  RelatedFiles = 'related-files',
}

export enum IntegrationExchangeTypeEnum {
  ClaimAcknowledgment = '277',
  ImplementationAcknowledgment = '999',
  File = 'file',
  Remit = 'RMT',
  Claim = 'claim',
  InsuranceEligibilityRequest = '270',
  InsuranceEligibilityResponse = '271',
  /** Connector provider-registration request (not an X12 document). */
  RegistrationRequest = 'reg-req',
  /** Connector provider-registration response. */
  RegistrationResponse = 'reg-res',
  /** Connector provider-registration cancellation request. */
  RegistrationCancellationRequest = 'unreg-req',
  /** Connector provider-registration cancellation response. */
  RegistrationCancellationResponse = 'unreg-res',
  /** Connector payer-enrollment request (not an X12 document). */
  EnrollmentRequest = 'enroll-req',
  /** Connector payer-enrollment response, including synchronous acknowledgements and events. */
  EnrollmentResponse = 'enroll-res',
  /** Singleton metadata retrieval performed as part of a connector workflow. */
  MetadataGet = 'x-meta-get',
  /** Singleton metadata update performed as part of a connector workflow. */
  MetadataPut = 'x-meta-put',
  /** Singleton document upload performed as part of a connector workflow. */
  DocumentPut = 'x-doc-put',
  /** Singleton document download performed as part of a connector workflow. */
  DocumentGet = 'x-doc-get',
  /**
   * Connector transaction poll. Vocabulary only today — no exchange row is
   * written for a poll; the member exists so key routing
   * (`specifics.keyRouting`) can address the call.
   */
  Poll = 'poll',
  /**
   * Connector payor-directory search/retrieve. Vocabulary only today — no
   * exchange row is written; exists so key routing can address the call.
   */
  PayorDirectory = 'payor-directory',
}

/** Every persisted IntegrationExchange type handled by the connector FIFO and its backstop. */
export const ConnectorTransmitExchangeTypes = Object.freeze([
  IntegrationExchangeTypeEnum.Claim,
  IntegrationExchangeTypeEnum.RegistrationRequest,
  IntegrationExchangeTypeEnum.EnrollmentRequest,
  IntegrationExchangeTypeEnum.EnrollmentResponse,
  IntegrationExchangeTypeEnum.DocumentPut,
] as const);

export type ConnectorTransmitExchangeType = (typeof ConnectorTransmitExchangeTypes)[number];

/**
 * Connection API workflow records belong in the Enrollment Portal, not EDI Activity —
 * except rows whose disposition is Rejected or Error, which the EDI Activity list
 * still shows so a failed registration or enrollment is visible there.
 * When the clinic's vendor can register and enroll (12384625988), EDI Activity
 * shows every registration and enrollment row; the x-* sub-steps keep the
 * failed-only rule.
 */
export const IntegrationExchangeUiOmitTypes: Partial<Record<IntegrationExchangeTypeEnum, true>> = {
  [IntegrationExchangeTypeEnum.RegistrationRequest]: true,
  [IntegrationExchangeTypeEnum.RegistrationResponse]: true,
  [IntegrationExchangeTypeEnum.RegistrationCancellationRequest]: true,
  [IntegrationExchangeTypeEnum.RegistrationCancellationResponse]: true,
  [IntegrationExchangeTypeEnum.EnrollmentRequest]: true,
  [IntegrationExchangeTypeEnum.EnrollmentResponse]: true,
  [IntegrationExchangeTypeEnum.MetadataGet]: true,
  [IntegrationExchangeTypeEnum.MetadataPut]: true,
  [IntegrationExchangeTypeEnum.DocumentPut]: true,
  [IntegrationExchangeTypeEnum.DocumentGet]: true,
};

/** One committed exchange row handed to the connector FIFO worker. */
export type ConnectorTransmitMessage = {
  exchangeId: string;
  clinicId: number;
};

export type ConnectorManualRunOperation = 'receive' | 'transmit';
export type ConnectorManualRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export type ConnectorManualReceiveRunSummary = {
  outcome: 'completed' | 'locked' | 'throttled' | 'disabled';
  polled: number;
  pages: number;
  passes: number;
  durationMs: number;
  deferred?: 'page-cap' | 'time-budget';
  outcomes: Record<string, number>;
};

export type ConnectorManualTransmitRunSummary = {
  reEnqueued: number;
  transmitted: number;
  enrollmentChecks: number;
  failed: number;
  lockedOut: number;
};

/** Durable lifecycle for one manual Receive or Transmit request. */
export type ConnectorManualRun = {
  runId: string;
  operation: ConnectorManualRunOperation;
  vendor: KnownClearingHouses;
  status: ConnectorManualRunStatus;
  requestedBy: string;
  requestedAt: number;
  startedAt?: number;
  completedAt?: number;
  summary?: ConnectorManualReceiveRunSummary | ConnectorManualTransmitRunSummary;
  error?: string;
};

export type ConnectorManualRunRecordOptions = {
  throw?: boolean;
};

export type ConnectorManualRunKickResult = {
  run: ConnectorManualRun;
  kicked: 'async' | 'failed';
};

/**
 * Ops-console "purge transmit queue" — the outcome, phrased for the console.
 * AWS enforces the cooldown itself (one PurgeQueue per queue per 60 seconds,
 * `PurgeQueueInProgress` inside the window); `nextAllowedAt` just lets the
 * clicker's button count the window down.
 */
export type ConnectorQueuePurgeResult = {
  purged: boolean;
  message: string;
  by?: string;
  nextAllowedAt?: number;
};

export type ConnectorInboundDrainMessage = {
  vendor?: string;
  holder?: string;
  /** Legacy spelling of `trigger: 'manual'`; the drain maps it. */
  force?: boolean;
  trigger?: ConnectorDrainTrigger;
  run?: ConnectorManualRun;
};

/** One async invocation of the connector transmit backstop. */
export type ConnectorTransmitBackstopMessage = {
  holder: string;
  vendor?: KnownClearingHouses;
  target?: ConnectorTransmitMessage;
  olderThanMs?: number;
  run?: ConnectorManualRun;
};
/**
 * What the shared 277CA consumer actually matched and applied.
 *
 * Parsing a document successfully is not the same as applying it: a valid 277
 * can contain no claim statuses, or name a control number that is absent from
 * the clinic. Connector submission-failure routing uses these identities to decide
 * whether the 277 rejection owns telemetry/status movement or the functional
 * rejection fallback must do so. Each array carries the claim identity; callers
 * derive counts from `.length` rather than maintaining parallel counters.
 */
export type ClaimAcknowledgementApplicationResult = {
  matchedClaims: number[];
  rejectedClaims: number[];
  acceptedClaims: number[];
  unmatchedClaims: string[];
  outOfScopeClaims: number[];
};

const AllIntegrationExchangeTypeOptions: SELECT_OPTION_TYPE[] = [
  {
    text: 'Claim Acknowledgment (277)',
    value: IntegrationExchangeTypeEnum.ClaimAcknowledgment,
  },
  {
    text: 'Implementation Acknowledgment (999)',
    value: IntegrationExchangeTypeEnum.ImplementationAcknowledgment,
  },
  // { text: 'File', value: IntegrationExchangeTypeEnum.File }, // Why upload a standard file??? Attachment maybe?
  { text: 'Remit', value: IntegrationExchangeTypeEnum.Remit },
  { text: 'Claim', value: IntegrationExchangeTypeEnum.Claim },
  {
    text: 'Insurance Eligibility Request (270)',
    value: IntegrationExchangeTypeEnum.InsuranceEligibilityRequest,
  },
  {
    text: 'Insurance Eligibility Response (271)',
    value: IntegrationExchangeTypeEnum.InsuranceEligibilityResponse,
  },
  {
    text: 'Registration Request',
    value: IntegrationExchangeTypeEnum.RegistrationRequest,
  },
  {
    text: 'Registration Response',
    value: IntegrationExchangeTypeEnum.RegistrationResponse,
  },
  {
    text: 'Registration Cancellation Request',
    value: IntegrationExchangeTypeEnum.RegistrationCancellationRequest,
  },
  {
    text: 'Registration Cancellation Response',
    value: IntegrationExchangeTypeEnum.RegistrationCancellationResponse,
  },
  {
    text: 'Enrollment Request',
    value: IntegrationExchangeTypeEnum.EnrollmentRequest,
  },
  {
    text: 'Enrollment Response',
    value: IntegrationExchangeTypeEnum.EnrollmentResponse,
  },
];

export const IntegrationExchangeTypeOptions = AllIntegrationExchangeTypeOptions.filter(
  option => !IntegrationExchangeUiOmitTypes[option.value as IntegrationExchangeTypeEnum]
);

/**
 * EDI Activity labels for the Connection API workflow rows that surface there
 * (Rejected/Error only \u2014 see IntegrationExchangeUiOmitTypes). Billers know
 * "Registration" and "Enrollment", not the wire step names; the metadata and
 * document exchanges are enrollment sub-steps (task reads, document uploads
 * and confirmations), so they read as Enrollment too.
 */
const connectorWorkflowDisplayByType: Partial<Record<IntegrationExchangeTypeEnum, string>> = {
  [IntegrationExchangeTypeEnum.RegistrationRequest]: 'Registration',
  [IntegrationExchangeTypeEnum.RegistrationResponse]: 'Registration',
  [IntegrationExchangeTypeEnum.RegistrationCancellationRequest]: 'Registration',
  [IntegrationExchangeTypeEnum.RegistrationCancellationResponse]: 'Registration',
  [IntegrationExchangeTypeEnum.EnrollmentRequest]: 'Enrollment',
  [IntegrationExchangeTypeEnum.EnrollmentResponse]: 'Enrollment',
  [IntegrationExchangeTypeEnum.MetadataGet]: 'Enrollment',
  [IntegrationExchangeTypeEnum.MetadataPut]: 'Enrollment',
  [IntegrationExchangeTypeEnum.DocumentPut]: 'Enrollment',
  [IntegrationExchangeTypeEnum.DocumentGet]: 'Enrollment',
};

const vals = Object.values(IntegrationExchangeTypeEnum);
export const IntegrationExchangeDisplayByType: STRING_STRING_HASH = {
  ...Object.keys(IntegrationExchangeTypeEnum)
    .map((key, idx) => ({ text: key, value: vals[idx] }))
    .reduce((acc, cur) => {
      acc[cur.value] = cur.text
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/ /g, '\u00A0');

      return acc;
    }, {} as STRING_STRING_HASH),
  ...connectorWorkflowDisplayByType,
};

export const IntegrationExchangeTypeByExtension = {
  '277': IntegrationExchangeTypeEnum.ClaimAcknowledgment,
  '27c': IntegrationExchangeTypeEnum.ClaimAcknowledgment,
  '999': IntegrationExchangeTypeEnum.ImplementationAcknowledgment,
  rmt: IntegrationExchangeTypeEnum.Remit,
  '270': IntegrationExchangeTypeEnum.InsuranceEligibilityRequest,
  '271': IntegrationExchangeTypeEnum.InsuranceEligibilityResponse,
};

export enum IntegrationExchangeDispositionEnum {
  Pending = 'pending',
  Transferred = 'transferred',
  /** Enrollment is accepted by the vendor but cannot progress until the provider completes a task. */
  ActionRequired = 'action-required',
  Rejected = 'rejected',
  Accepted = 'accepted',
  Processed = 'processed',
  Error = 'error',
  ChecksumFailure = 'checksumFailure',
  Reversed = 'reversed',
  /**
   * Parked at clinicId 0 for a human (plan §3e.7 — the unrouted queue IS this
   * disposition, no separate table). Nothing vendor-related is ever dropped:
   * artifacts that can't be processed archive as durable Unrouted rows.
   */
  Unrouted = 'unrouted',
  /**
   * A worked Unrouted row a human CLOSED (not ours / resolved without routing),
   * still at clinicId 0 (plan §3e.7). Distinct from `Error` so it (a) stops the
   * unrouted alarm, (b) is honestly "human-resolved, never posted" — not a
   * retryable failure, and (c) stays visible + auditable in the admin queue's
   * Resolved view (its only possible surface). Reversible back to `Unrouted`
   * via reopen. Never shown to a clinic (clinicId 0, and filtered like Unrouted).
   */
  Resolved = 'resolved',
  Unsupported = 'unsupported',
}

export const IntegrationExchangeUiOmitDispositions = {
  [IntegrationExchangeDispositionEnum.Unrouted]: true,
  [IntegrationExchangeDispositionEnum.Resolved]: true,
  [IntegrationExchangeDispositionEnum.Unsupported]: true,
};

/**
 * Where a `meta.dispositionHistory` entry came from — the exchange-disposition
 * analogue of `InvoiceStatusDataSource`. Stored as the compact NUMERIC value on
 * the entry's `data.src` (payload size — clinics rack up huge transition counts,
 * so the wire carries the number, not a sentence). Map to a human label at the
 * display boundary via `exchangeDispositionDataSourceLabel`. Add a member per new
 * writer; never reuse a number.
 */
export enum ExchangeDispositionDataSource {
  UnroutedClose = 20,
  UnroutedReopen = 40,
  ConnectorSubmissionConnectionIssue = 60,
  /**
   * A reprocess routed the document and moved it to its clinic — `Unsupported` →
   * `Pending`/`Processed`. The entry carries the comment the row HAD, because the
   * comment is replaced on success and the original ("clinic could not be determined")
   * would otherwise be lost.
   */
  UnsupportedReprocessRouted = 100,
  /**
   * A reprocess understood the document but could not determine its clinic —
   * `Unsupported` → `Unrouted`. Not a failure: it stopped being a code question and
   * became a human one.
   */
  UnsupportedReprocessUnrouted = 120,
  /**
   * A human closed an `Unsupported` row: the document is genuinely not worth handling
   * (garbled, foreign, junk), so it stops appearing in the queue. Distinct from
   * `UnroutedClose` so the trail says WHICH question was being abandoned — "we can't
   * process this" versus "this isn't ours".
   */
  UnsupportedClose = 140,
  /**
   * A human reopened a dismissed `Unsupported` row (restored via `meta.closedFrom`).
   * Distinct from `UnroutedReopen` for the same reason the closes are distinct: the
   * trail says which question was picked back up.
   */
  UnsupportedReopen = 160,
}

/** Compact `src` → human label — mapped at the response/UI boundary, never stored on the wire. */
export const ExchangeDispositionDataSourceLabels: Record<ExchangeDispositionDataSource, string> = {
  [ExchangeDispositionDataSource.UnroutedClose]: 'Unrouted close',
  [ExchangeDispositionDataSource.UnroutedReopen]: 'Unrouted reopen',
  [ExchangeDispositionDataSource.ConnectorSubmissionConnectionIssue]:
    'Connector submission connection issue',
  [ExchangeDispositionDataSource.UnsupportedReprocessRouted]: 'Unsupported reprocess — routed',
  [ExchangeDispositionDataSource.UnsupportedReprocessUnrouted]: 'Unsupported reprocess — unrouted',
  [ExchangeDispositionDataSource.UnsupportedClose]: 'Unsupported close',
  [ExchangeDispositionDataSource.UnsupportedReopen]: 'Unsupported reopen',
};

export const exchangeDispositionDataSourceLabel = (
  src?: ExchangeDispositionDataSource | null
): string => (src == null ? '' : (ExchangeDispositionDataSourceLabels[src] ?? ''));

export const canRetryExchange: (
  exchange: IntegrationExchangeRow | IntegrationExchangeRowPartial
) => boolean = (exchange: IntegrationExchangeRow | IntegrationExchangeRowPartial) => {
  if (!exchange || !exchange?.type || !exchange?.disposition) return false;

  return (
    exchange.disposition === IntegrationExchangeDispositionEnum.Pending ||
    exchange.disposition === IntegrationExchangeDispositionEnum.Error ||
    exchange.disposition === IntegrationExchangeDispositionEnum.Reversed
  );
};

const vals2 = Object.values(IntegrationExchangeDispositionEnum);
export const IntegrationExchangeDispositionDisplay: STRING_STRING_HASH = Object.keys(
  IntegrationExchangeDispositionEnum
)
  .map((key, idx) => ({ text: key, value: vals2[idx] }))
  .reduce((acc, cur) => {
    acc[cur.value] = cur.text.replace(/([A-Z])/g, ' $1').trim();

    return acc;
  }, {} as STRING_STRING_HASH);

export enum IntegrationExchangeDirection {
  Partner = 'partner',
  S3 = 's3',
  User = 'user',
}

export type IntegrationExchangeInsertType = {
  integrationId: number;
  clinicId: number;
  exchangeId: string;
  ts?: number;
  touched?: number;
  userId: string;
  type: IntegrationExchangeTypeEnum;
  source: IntegrationExchangeDirection;
  sourcePath: string;
  target: IntegrationExchangeDirection;
  targetPath: string;
  archive?: string;
  archivePath?: string;
  disposition: IntegrationExchangeDispositionEnum;
  comment: string;
  bytes: number;
  checksum?: string;
  meta?: IntegrationExchangeMetaType;
};

export type IntegrationExchangeRow = {
  id: number;
  clinicId: number;
  integrationId: number;
  exchangeId: string;
  ts: number;
  touched?: number;
  userId?: string;
  type: string;
  source: string;
  sourcePath?: string;
  target?: string;
  targetPath?: string;
  archive?: string;
  archivePath?: string;
  disposition: string;
  comment?: string;
  bytes?: number;
  checksum?: string;
  checksum_fuzzy?: string;
  meta?: any;
  originalExchangeId?: string;
};

export type IntegrationExchangeRowPartial = {
  id: number;
  disposition: IntegrationExchangeDispositionEnum;
  type: IntegrationExchangeTypeEnum;
  source?: string;
  userId?: string;
};

export type IntegrationExchangeType = IntegrationExchangeInsertType & {
  id: number;
  userDisplayName?: string;
  billingProfileId?: number; // Sometimes, we add this to the exchange record. It is not in the table!
  tz?: string;
  queueCount?: number; // Count of pending EraClaimResponseQueue items (RMT only)
  pseudo835?: Pseudo835Capability;
  /**
   * Whether this exchange can be re-driven — computed ENTIRELY server-side by
   * `isExchangeRetryable` (server-core) and stamped by the single-exchange read.
   * The GET sets it; the retry POST runs the same computation to enforce it; the
   * UI is a pure viewer of this flag. Not a stored column.
   */
  retryable?: boolean;
  /**
   * The connector vendor when this exchange rides an `api` Integration row (else
   * null) — joined in by the exchange reads so `isExchangeRetryable` can decide
   * PURELY, without a second fetch. Legacy SFTP rows are null here. Not a stored
   * column on IntegrationExchange.
   */
  connectorVendor?: KnownClearingHouses | null;
};

export type IntegrationExchangeMetaActivityType = {
  ts: number;
  userId?: string;
  userDisplayName?: string;
  event: IntegtrationExchangeMetaEventEnum;
  detail: string;
};

export enum IntegtrationExchangeMetaEventEnum {
  Created = 'created',
  Download = 'download',
  EraDeleted = 'era-deleted',
  ReconcilePaymentAmount = 'reconcile-payment-amount',
  Reprocessed = 'reprocessed',
  SetDisposition = 'set-disposition',
  Sync = 'sync',
}

/**
 * The PHI namespace on exchange `meta` (plan §3c.6): small patient-bearing
 * siblings (the vendor's submit response/errors) live under `meta.phi.*`; ONE
 * scrubber strips the whole `phi` key at response boundaries (endpoints never
 * hand-pick fields to hide); detail endpoints that genuinely need the content
 * OPT IN explicitly. The document PAYLOAD itself lives in the
 * `IntegrationExchangePayload` sidecar table (`payload` below is legacy-read
 * only — pre-sidecar rows). Use the accessors in
 * `integration.helpers/connector/exchangePayload.ts` — never hand-build the nesting.
 */
export type IntegrationExchangeMetaPhiType = {
  /** LEGACY (pre-sidecar rows) — the exchanged document, read-only fallback. New payloads live in IntegrationExchangePayload; nothing writes this key anymore. */
  payload?: unknown;
  /** The vendor's full submit response (may echo patient identifiers). */
  submitResponse?: unknown;
  /** Vendor edit/validation rejections, verbatim. */
  submitErrors?: unknown[];
};

export type IntegrationExchangeMetaType = {
  activity?: IntegrationExchangeMetaActivityType[];
  originalExchangeId?: string;
  /** PHI-free participant provisioning and vendor-enrollment lifecycle. */
  enrollment?: ConnectorEnrollmentState;
  /** PHI-free clinic portal registration/mapping identity. */
  connectionApi?: ConnectionApiExchangeState;
  /** Correlation for one singleton task/document operation under an enrollment request. */
  connectionApiTask?: ConnectionApiTaskExchangeState;
  soapError?: string;
  phi?: IntegrationExchangeMetaPhiType;
  /**
   * FETCH-STUB marker (INFRA-6): the vendor fetch for this inbound ref failed,
   * so the row parks the ref (arming layer-1 dedupe — the cursor moves on)
   * with NO stored payload. Retry = redispatch re-FETCHES instead of reading
   * the sidecar. PHI-free by construction (there is no document).
   */
  fetchError?: {
    message: string;
    at: number;
    /** The ref's receivedAt (epoch ms), so the rebuilt ref matches the discovery. */
    receivedAt?: number;
  };
  /**
   * Audit breadcrumbs left when a fetch-stub is upgraded in place: the
   * resolved `fetchError` plus when the retry succeeded. Its presence marks
   * a row that was once a stub; clearing `fetchError` itself is what stops
   * future retries from taking the re-fetch path.
   */
  fetchErrorHistory?: {
    message: string;
    at: number;
    receivedAt?: number;
    retriedAt: number;
  }[];
};

export enum IntegrationTypeEnum {
  EDI = 'edi',
  ManualEDI = 'manual-edi',
  InsuranceEligibility = 'insurance-eligibility',
  /** API-first vendors (integration connectors) — incl. clinicId = 0 platform rows. */
  API = 'api',
}

export enum IntegrationStepsEnum {
  Ready = 'ready',
  QueuedForFiles = 'queuedForFiles',
  FilesReceived = 'filesReceived',
  NoFilesAvailable = 'noFilesAvailable',
  Off = 'off',
}

export type IntegrationHelperEnqueueContext = {
  integration: IntegrationPartnerType;
  userId: string;
  data: string | Buffer;
  claimContext?: ClaimBuildContext; // elec. claims only. this is the createBatchSet STRUCTURE stored as the exchange's CMS-1500 submission snapshot
};

export enum IntegrationDestinationQueue {
  ClaimsOutbox = 'claims-outbox',
  ManualEDI = 'manual-edi',
  PaperClaims = 'paper-claims',
  InsuranceEligibility = 'insurance-eligibility',
}

export type IntegrationPartnerType = {
  id: number;
  name: string;
  type: IntegrationTypeEnum;
  controlNumber: number;
  testSiteId: string;
  productionSiteId: string;
  specifics: string;
  exchangeVersionCode: string;
  appReceiverCode: string;
  senderIdQualifier: string;
  senderId: string;
  receiverIdQualifier: string;
  receiverId: string;
  default: boolean;
  active: boolean;
  clinicId: number;
  billingProfileId: number;
  secretDecoded: IntegrationSecretType;
  production: boolean;
  integratedClearinghouse: KnownClearingHouses.TriZetto | KnownClearingHouses.Infinedi;
  lastProcessedAt?: number | null;
  credentialsValid?: boolean | null;
  credentialsLastCheckedAt?: number | null;
  sftpConsecutiveFailures?: number;
  sftpConsecutiveAuthFailures?: number;
  sftpBackoffUntil?: number | null;
  sftpLastFailureAt?: number | null;
  sftpLastErrorCode?: string | null;
  ediConsecutiveProcessingFailures?: number;
  ediProcessingBackoffUntil?: number | null;
  ediLastProcessingFailureAt?: number | null;
  ediLastProcessingErrorCode?: string | null;
};

export type SftpCredentialCheckMessage = {
  clinicId: number;
  integrationIds: number[];
  integratedClearinghouse: KnownClearingHouses.TriZetto | KnownClearingHouses.Infinedi;
};

/**
 * A platform-scoped (clinicId = 0) Integration row for an API connector
 * vendor (docs/connection-api/INFRASTRUCTURE_PLAN.md §0). Same row shape as
 * `IntegrationPartnerType`, but the vendor can be ANY `KnownClearingHouses`
 * member — the SFTP/EDI union above stays narrow on purpose so existing EDI
 * consumers keep their exact typing.
 */
export type PlatformIntegrationRowType = Omit<IntegrationPartnerType, 'integratedClearinghouse'> & {
  integratedClearinghouse: KnownClearingHouses;
};

export type IntegrationSecretType = {
  host: string;
  port: number;
  as: string;
  sky: string;
  path: string;
};

export type IntegrationRemoteScriptType = {
  action?: string;
  args?: STRING_ANY_HASH;
};

export type IntegrationSummaryCountRowtype = {
  key: string;
  billingProfileId: number;
  billingProfileName: string;
  hits: number;
};

export type IntegrationSummaryType = {
  counts: IntegrationSummaryCountRowtype[];
};

// Claims-tab "Filter by Issue" multi-select
export const ClaimIssueType = {
  Flagged: 'Flagged',
  InactivePolicy: 'InactivePolicy',
  TestingErrors: 'TestingErrors',
  MissingDiagnosis: 'MissingDiagnosis',
  OpenServiceLines: 'OpenServiceLines',
  DisputedServiceLines: 'DisputedServiceLines',
} as const;
export type ClaimIssueType = (typeof ClaimIssueType)[keyof typeof ClaimIssueType];

export type IntegrationInvoiceFilterType = {
  billingProfileId?: string;
  days?: string;
  startDate?: string;
  endDate?: string;
  patientId?: string;
  renderingProviderId?: string;
  payorId?: string | number | (string | number)[];
  status?: InvoiceStatusEnum[];
  automation?: string;
  billingPriority?: 'primary' | 'secondary' | 'tertiary' | 'other';
  quickPick?: ClaimQuickPickKey;
  allTime?: boolean;
  isFlagged?: boolean;
  timezone?: string;
  hasOpenLines?: boolean;
  issueTypes?: ClaimIssueType[];
  adjudicationGroupCode?: string; // CO/PI/PR/OA (ClaimAdjustmentGroupCodes keys)
  adjudicationReasonCodes?: string[]; // CARC codes; with a group code set, the reason must be inside that group's adjustment.
  procedureCodes?: string[];
};

export type IntegrationInvoiceQueryOptions = {
  getInfoForBatchClaims?: boolean;
  includeCount?: boolean;
  includeStatusSummary?: boolean;
  includeQuickPickSummary?: boolean;
  summaryOnly?: boolean;
  // Populates IntegrationInvoice.notes with the note bodies (CSV export only).
  // Costs one extra query per page, so the grid leaves it off.
  includeNoteText?: boolean;
};

export type IntegrationExchangeFilterType = {
  createdDays?: string;
  createdStartDate?: string;
  createdEndDate?: string;
  userId?: string;
  billingProfileId?: string | null;
  disposition?: IntegrationExchangeDispositionEnum[];
  type?: IntegrationExchangeTypeEnum[];
  source?: IntegrationExchangeDirection;
  target?: IntegrationExchangeDirection;
  missingEra?: boolean;
  /**
   * EDI Activity (12384625988) filters. `displayStatus` replaces `disposition`
   * when present: empty means every visible status. Callers that still send
   * `disposition` without `displayStatus` keep the raw disposition filter.
   */
  displayStatus?: ExchangeDisplayStatus[];
  providerId?: string;
  patientId?: string;
  payorId?: number;
  /** Date of service, YYYY-MM-DD in the clinic's timezone. */
  dos?: string;
  /** Our claim identity (Invoice.controlNumber, CLM01 = CLP01). Numeric. */
  claimControlNumber?: string;
  /** The payor's claim number (CLP07 / REF*F8). */
  payorClaimControlNumber?: string;
  /** IntegrationExchange.partnerTransactionId; connector rows only. */
  vendorTransactionId?: string;
  /** EraTraceNumber.traceNumber (835 TRN02). */
  checkNumber?: string;
  /** Attach per-row summaries to the UIList response. */
  summaries?: boolean;
};

/**
 * EDI Activity (12384625988) display statuses: the five spec buckets plus
 * Reversed on its own. Emily's 2026-09-21 map. The three platform-queue
 * dispositions (unrouted, resolved, unsupported) belong to no bucket and never
 * reach a clinic screen.
 */
export enum ExchangeDisplayStatus {
  Generated = 'generated',
  Processed = 'processed',
  Error = 'error',
  Rejected = 'rejected',
  Duplicate = 'duplicate',
  Reversed = 'reversed',
}

/** ONE source of truth; `exchangeDisplayStatus` is derived from it. */
export const ExchangeDisplayStatusDispositions: Record<
  ExchangeDisplayStatus,
  IntegrationExchangeDispositionEnum[]
> = {
  [ExchangeDisplayStatus.Generated]: [IntegrationExchangeDispositionEnum.Pending],
  [ExchangeDisplayStatus.Processed]: [
    IntegrationExchangeDispositionEnum.Processed,
    IntegrationExchangeDispositionEnum.Transferred,
    IntegrationExchangeDispositionEnum.Accepted,
  ],
  [ExchangeDisplayStatus.Error]: [
    IntegrationExchangeDispositionEnum.Error,
    IntegrationExchangeDispositionEnum.ActionRequired,
  ],
  [ExchangeDisplayStatus.Rejected]: [IntegrationExchangeDispositionEnum.Rejected],
  [ExchangeDisplayStatus.Duplicate]: [IntegrationExchangeDispositionEnum.ChecksumFailure],
  [ExchangeDisplayStatus.Reversed]: [IntegrationExchangeDispositionEnum.Reversed],
};

export const ExchangeDisplayStatusLabel: Record<ExchangeDisplayStatus, string> = {
  [ExchangeDisplayStatus.Generated]: 'Generated',
  [ExchangeDisplayStatus.Processed]: 'Processed',
  [ExchangeDisplayStatus.Error]: 'Error',
  [ExchangeDisplayStatus.Rejected]: 'Rejected',
  [ExchangeDisplayStatus.Duplicate]: 'Duplicate',
  [ExchangeDisplayStatus.Reversed]: 'Reversed',
};

const dispositionToDisplayStatus = (
  Object.keys(ExchangeDisplayStatusDispositions) as ExchangeDisplayStatus[]
).reduce(
  (acc, status) => {
    ExchangeDisplayStatusDispositions[status].forEach(disposition => {
      acc[disposition] = status;
    });

    return acc;
  },
  {} as Partial<Record<IntegrationExchangeDispositionEnum, ExchangeDisplayStatus>>
);

/** Null for the hidden platform-queue dispositions and unknown values. */
export const exchangeDisplayStatus = (disposition?: string | null): ExchangeDisplayStatus | null =>
  disposition
    ? (dispositionToDisplayStatus[disposition as IntegrationExchangeDispositionEnum] ?? null)
    : null;

/**
 * Dispositions behind a display-status selection. An empty selection means
 * every visible status, never "any disposition" (which would admit the
 * platform-queue states).
 */
export const dispositionsForDisplayStatuses = (
  statuses?: ExchangeDisplayStatus[] | null
): IntegrationExchangeDispositionEnum[] => {
  const chosen =
    statuses && statuses.length > 0
      ? statuses
      : (Object.keys(ExchangeDisplayStatusDispositions) as ExchangeDisplayStatus[]);

  return chosen.flatMap(status => ExchangeDisplayStatusDispositions[status] ?? []);
};

export type ExchangeFlowDirection = 'inbound' | 'outbound';

const outboundExchangeTypes: Partial<Record<IntegrationExchangeTypeEnum, true>> = {
  [IntegrationExchangeTypeEnum.Claim]: true,
  [IntegrationExchangeTypeEnum.InsuranceEligibilityRequest]: true,
  [IntegrationExchangeTypeEnum.RegistrationRequest]: true,
  [IntegrationExchangeTypeEnum.RegistrationCancellationRequest]: true,
  [IntegrationExchangeTypeEnum.EnrollmentRequest]: true,
  [IntegrationExchangeTypeEnum.MetadataPut]: true,
  [IntegrationExchangeTypeEnum.DocumentPut]: true,
};

/**
 * Semantic direction by document type. `source`/`target` are transport hops
 * (user, s3, partner), not direction; a synchronous 270/271 is one row holding
 * both legs and reads as the request the user sent.
 */
export const exchangeDirection = (type?: string | null): ExchangeFlowDirection =>
  type && outboundExchangeTypes[type as IntegrationExchangeTypeEnum] ? 'outbound' : 'inbound';

/** Row labels per the EDI Activity mock. Falls back to the generic label. */
export const EdiActivityTypeLabel: Partial<Record<IntegrationExchangeTypeEnum, string>> = {
  [IntegrationExchangeTypeEnum.Claim]: 'Claim Submission',
  [IntegrationExchangeTypeEnum.ImplementationAcknowledgment]: 'File Acknowledgement',
  [IntegrationExchangeTypeEnum.Remit]: 'Remittance',
  [IntegrationExchangeTypeEnum.ClaimAcknowledgment]: 'Claim Acknowledgement',
  [IntegrationExchangeTypeEnum.InsuranceEligibilityRequest]: 'Eligibility Request',
  [IntegrationExchangeTypeEnum.InsuranceEligibilityResponse]: 'Eligibility Response',
  [IntegrationExchangeTypeEnum.RegistrationRequest]: 'Registration',
  [IntegrationExchangeTypeEnum.RegistrationResponse]: 'Registration',
  [IntegrationExchangeTypeEnum.RegistrationCancellationRequest]: 'Registration',
  [IntegrationExchangeTypeEnum.RegistrationCancellationResponse]: 'Registration',
  [IntegrationExchangeTypeEnum.EnrollmentRequest]: 'Enrollment',
  [IntegrationExchangeTypeEnum.EnrollmentResponse]: 'Enrollment',
};

/** The X12 code shown as the row sub-tag for non-claim types. */
export const EdiActivityTypeCode: Partial<Record<IntegrationExchangeTypeEnum, string>> = {
  [IntegrationExchangeTypeEnum.ImplementationAcknowledgment]: '999',
  [IntegrationExchangeTypeEnum.Remit]: '835',
  [IntegrationExchangeTypeEnum.ClaimAcknowledgment]: '277CA',
  [IntegrationExchangeTypeEnum.InsuranceEligibilityRequest]: '270',
  [IntegrationExchangeTypeEnum.InsuranceEligibilityResponse]: '271',
};

/**
 * Types shown in full on EDI Activity when the vendor can register and enroll.
 * The x-* sub-steps are not here: they stay failed-only (EdiEnrollmentSubStepTypes).
 */
export const EdiTypeFamilies = {
  registration: [
    IntegrationExchangeTypeEnum.RegistrationRequest,
    IntegrationExchangeTypeEnum.RegistrationResponse,
    IntegrationExchangeTypeEnum.RegistrationCancellationRequest,
    IntegrationExchangeTypeEnum.RegistrationCancellationResponse,
  ],
  enrollment: [
    IntegrationExchangeTypeEnum.EnrollmentRequest,
    IntegrationExchangeTypeEnum.EnrollmentResponse,
  ],
} as const;

/**
 * Enrollment sub-steps. The Enrollment type choice includes them so a failed
 * one (labeled Enrollment) is found there; the failed-only rule still hides
 * the rest.
 */
export const EdiEnrollmentSubStepTypes = [
  IntegrationExchangeTypeEnum.MetadataGet,
  IntegrationExchangeTypeEnum.MetadataPut,
  IntegrationExchangeTypeEnum.DocumentPut,
  IntegrationExchangeTypeEnum.DocumentGet,
] as const;

/** Filters answered by a lookup into another table (the "arm" registry). */
export type EdiArmFilterKey =
  | 'patientId'
  | 'payorId'
  | 'providerId'
  | 'dos'
  | 'checkNumber'
  | 'claimControlNumber'
  | 'payorClaimControlNumber';

export type EdiFilterSupportByType = Partial<
  Record<IntegrationExchangeTypeEnum, EdiArmFilterKey[]>
>;

export type ExchangeClaimFormat = '837' | 'JSON' | 'CMS-1500';

export type ExchangeAckResult = 'Accepted' | 'Accepted with Errors' | 'Rejected';

/**
 * Per-row summary attached to the UIList response when requested. Ledger
 * facts are null (not zero) when no ledger rows exist for the exchange.
 */
export type IntegrationExchangeSummary =
  | {
      type: IntegrationExchangeTypeEnum.Claim;
      format: ExchangeClaimFormat;
      claims: number | null;
      billed: number | null;
      /** Every invoice the file carried (ledger) plus those still pointing at it. */
      invoices: { id: number; number: string | null }[];
    }
  | {
      type: IntegrationExchangeTypeEnum.Remit;
      checkNumbers: string[];
      paid: number | null;
      remits: number | null;
      eraIds: number[];
      payorName: string | null;
      /** Per ERA in the file, distinct, as the ERA screen shows them: BPR04 code, BPR16 date, N1*PE name. */
      paymentMethods: string[];
      remitDates: string[];
      payeeNames: string[];
    }
  | {
      type: IntegrationExchangeTypeEnum.ImplementationAcknowledgment;
      result: ExchangeAckResult | null;
      vendor: string | null;
    }
  | {
      type: IntegrationExchangeTypeEnum.ClaimAcknowledgment;
      claims: number | null;
      accepted: number | null;
      rejected: number | null;
      vendor: string | null;
    }
  | {
      type:
        | IntegrationExchangeTypeEnum.InsuranceEligibilityRequest
        | IntegrationExchangeTypeEnum.InsuranceEligibilityResponse;
      patientName: string | null;
      payorName: string | null;
      eligibilityRequestId: number | null;
    };

export type IntegrationExchangeListRow = IntegrationExchangeType & {
  /** SHA-256 of the file (Technical details). */
  checksum?: string | null;
  /** The earlier exchange a Duplicate matched. */
  originalExchangeId?: string | null;
  /** Integration.integratedClearinghouse of the owning row; null when none. */
  vendor?: string | null;
  /** Integration.type of the owning row ('api' for connector vendors). */
  integrationType?: string | null;
  summary?: IntegrationExchangeSummary | null;
};

/** Stat tiles. All null when the window holds no rows for that figure. */
export type EdiActivityStats = {
  transactions: number | null;
  claimFiles: number | null;
  claims: number | null;
  totalBilled: number | null;
  remitFiles: number | null;
  remits: number | null;
  totalPaid: number | null;
};

/** Whether the clinic's connector vendor can register providers and enroll payors. */
export type EdiActivityCapabilities = {
  registration: boolean;
  enrollment: boolean;
  /** Only API (connector) vendors stamp partnerTransactionId; SFTP rows never carry one. */
  vendorTransactionId: boolean;
};

export type EdiActivityFilterOptions = {
  payorOptions: SELECT_OPTION_TYPE[];
  /** Registration/enrollment rows show on EDI Activity only when true. */
  capabilities: EdiActivityCapabilities;
  filterSupportByType: EdiFilterSupportByType;
};

/** One claim in a 999 or 277, from the ClinicTransactionEvent ledger. */
export type EdiLedgerDetailRow = {
  invoiceId: number | null;
  invoiceNumber: string | null;
  controlNumber: number | null;
  patientName: string | null;
  /** 999: Accepted / Accepted with Errors / Rejected. 277: Accepted / Rejected, else the raw action. */
  action: string | null;
  /** One line on why, for a claim that was not cleanly accepted; null otherwise. */
  reason: string | null;
  codes: {
    /** 277: 2200D claim level, 2220D service line. 999: 2000 / 2100 / 2110. */
    loop: string | null;
    segment: string | null;
    element: string | null;
    code: string | null;
    message: string | null;
  }[];
};

/**
 * A 999's answer for the whole file, read from the file itself: the TA1 and
 * AK9 results and any rejected claim that matched no invoice. The ledger
 * records none of these.
 */
export type EdiFileAcknowledgement = {
  result: ExchangeAckResult | null;
  reasons: string[];
  /** Every claim the file answered, matched to an invoice or not; null for an eligibility 999. */
  claims: number | null;
};

/** A claim file's 999s, or a 999's claim files, matched through the invoices each names. */
export type EdiRelatedFiles = {
  exchangeId: string;
  files: { exchangeId: string; type: IntegrationExchangeTypeEnum; ts: number }[];
};

export type EdiLedgerDetail = {
  exchangeId: string;
  rows: EdiLedgerDetailRow[];
  /** 999 only; null when the file could not be read. */
  file?: EdiFileAcknowledgement | null;
};

/**
 * Request context the transactions handler derives from the session and passes
 * to `exchange()`. Never read from the request body. Defaults apply only in
 * `exchange()`.
 */
export type ExchangeRequestContext = {
  /** Caller holds Biller or Admin; required for responses carrying patient names. */
  isBillerOrAdmin?: boolean;
  /** Clinic primary timezone for date-of-service ranges. */
  timezone?: string;
};

export type ClaimManualSearchFilterType = {
  skip?: number;
  limit?: number;
  page?: number;
  search?: string;
  patientId?: string;
  dateStart?: string;
  dateEnd?: string;
  billingProfileId?: number;
  payorId?: number;
};

export type EraFilterType = {
  createdDays?: number;
  createdStartDate?: string;
  createdEndDate?: string;
  startDate?: string;
  endDate?: string;
  remitStartDate?: string;
  remitEndDate?: string;
  status?: EraStatus[];
  controlNumber?: string;
  exchangeId?: string;
  patientId?: string;
  payorId?: number;
  amount?: number;
};

export type ClaimResponseFilterType = {
  createdDays?: number;
  createdStartDate?: string;
  createdEndDate?: string;
  userId?: string;
  billingProfileId?: number | string | null;
  status?: ClaimResponseEraStatus[];
  payorId?: number;
  patientId?: string;
  eraId?: number;
  providerId?: string;
  paymentAmount?: number;
  isFlagged?: boolean;
  sortBy?: 'patient' | 'dos' | 'amount' | 'status';
  sortDir?: 'asc' | 'desc';
  sortByOrders?: { by: 'patient' | 'dos' | 'amount' | 'status'; dir: 'asc' | 'desc' }[];
};

export const ERA_CLAIM_FILTER_DEFAULTS: ClaimResponseFilterType = {
  sortByOrders: [
    { by: 'patient', dir: 'asc' },
    { by: 'dos', dir: 'asc' },
  ],
};

export type InsuranceEligibilityRequestFilterType = {
  createdDays?: number;
  createdStartDate?: string;
  createdEndDate?: string;
  status?: InsuranceEligibilityRequestStatus[];
  patientId?: string;
  payorId?: string;
};

export const IntegrationDayOptions: SELECT_OPTION_TYPE[] = [
  {
    text: '24 Hours',
    value: '1',
  },
  {
    text: '3 Days',
    value: '3',
  },
  {
    text: '7 Days',
    value: '7',
  },
  {
    text: '30 Days',
    value: '30',
  },
  {
    text: '90 Days',
    value: '90',
  },
  {
    text: 'Range',
    value: '-1',
  },
];

export const IntegrationAutomationOptions: SELECT_OPTION_TYPE[] = [
  { text: '- any -', value: '' },
  {
    text: 'Electronic',
    value: '1',
  },
  {
    text: 'Paper',
    value: '0',
  },
];

export type FlaggedBillingProfile = {
  billingProfileId: number;
  credentialsValid: boolean | null;
  credentialsLastCheckedAt: number | null;
  billingProfileName: string;
  clearinghouse: string;
};

export type IntegrationExchangeDownloadArgs = {
  clinicId?: number;
  exchangeId?: string;
  trace?: boolean;
};
